import { Extension } from "@tiptap/core";
import { Node as PMNode } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

// A bare issue key like "KUI-123": a project prefix (a letter then letters/digits) + "-" + a number,
// immediately FOLLOWED by a delimiter (space/punctuation). The trailing-delimiter requirement is what
// makes the chip "commit" only after a word break, lets a single backspace (removing the delimiter)
// cancel it, and naturally skips quoted/backtick-wrapped keys ('KUI-1', `KUI-1`) — the closing quote
// sits between the key and any delimiter, so the lookahead fails.
const ISSUE_KEY_RE: RegExp = /\b[A-Z][A-Z0-9]*-\d+(?=[\s.,!?;:])/g;

export interface IssueKeyDecorationOptions {
  // Real project keys in the workspace; only keys with one of these prefixes are chipped.
  projectKeys: Set<string>;
}

// Overlay (decoration-only, never mutates the doc/markdown) that promotes real issue keys in the
// editor text to chips, matching the comment renderer. Clicks are handled by the host (a data
// attribute carries the key); see MarkdownField.
function buildDecorations(doc: PMNode, projectKeys: Set<string>): DecorationSet {
  if (projectKeys.size === 0) return DecorationSet.empty;
  const decorations: Decoration[] = [];
  doc.descendants((node: PMNode, pos: number) => {
    if (!node.isText || !node.text) return;
    const text: string = node.text;
    const re: RegExp = new RegExp(ISSUE_KEY_RE.source, "g");
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      const token: string = match[0];
      const prefix: string = token.slice(0, token.lastIndexOf("-"));
      if (!projectKeys.has(prefix)) continue;
      const from: number = pos + match.index;
      decorations.push(
        Decoration.inline(from, from + token.length, {
          class: "kuika-board__issue-chip",
          nodeName: "span",
          "data-issue-key": token
        })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
}

export const IssueKeyDecoration: Extension<IssueKeyDecorationOptions> = Extension.create<IssueKeyDecorationOptions>({
  name: "issueKeyDecoration",
  addOptions() {
    return { projectKeys: new Set<string>() };
  },
  addProseMirrorPlugins() {
    const projectKeys: Set<string> = this.options.projectKeys;
    // A fresh key PER editor instance — a shared module-level key collides when more than one editor
    // (description + comment composers) mounts this extension, and the second one's decorations vanish.
    const key: PluginKey<DecorationSet> = new PluginKey<DecorationSet>("issueKeyDecoration");
    return [
      new Plugin<DecorationSet>({
        key,
        state: {
          init: (_config, instance: EditorState): DecorationSet => buildDecorations(instance.doc, projectKeys),
          apply: (tr, old: DecorationSet): DecorationSet =>
            tr.docChanged ? buildDecorations(tr.doc, projectKeys) : old
        },
        props: {
          decorations(state: EditorState): DecorationSet | undefined {
            return key.getState(state);
          }
        }
      })
    ];
  }
});
